/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { Fragment, useState } from 'react'
import { css, jsx } from '@emotion/react'

import type { EuiSuperSelectOption } from '@elastic/eui'
import {
  EuiBetaBadge,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
  EuiSuperSelect,
  EuiText,
  EuiToolTip,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import Feature from '@modules/utils/feature'
import {
  ApiAccessSelectorTitle,
  CloudApiAccessDescription,
  CloudEsKibanaApiAccessDescription,
  getCloudAPIAccessLabel,
} from '@modules/role-assignments-lib/messages'

// eslint-disable-next-line import/no-restricted-paths
import { isFeatureActivated } from '@/store'

import { ApiAccessOptions } from './types'
import LimitedAccessTooltip from './LimitedAccessTooltip'

import type { ApiAccessSelectorProps } from './types'
import type { FunctionComponent } from 'react'

const ApiAccessSelector: FunctionComponent<ApiAccessSelectorProps> = ({
  onChange,
  defaultValue,
  includeLabel = true,
  fullWidth = false,
  cssStyle = {},
  projectId,
  isDisabled,
  containsCustomRoles = false,
  containsNonCloudSpecificProjectRoles = false,
  isApiKey = false,
  resourceType,
}) => {
  const isGovCloud = isFeatureActivated(Feature.hideIrrelevantSectionsFromGovCloud)

  const [currentValue, setCurrentValue] = useState(defaultValue)
  const [_, flags] = useFlagsWhenLoaded()

  const isUIAMEnabled = flags.uiamUi

  const onChangeValue = (value: ApiAccessOptions) => {
    setCurrentValue(value)
    onChange(value)
  }

  const { euiTheme } = useEuiTheme()

  // API access selector functionality is only available and used in org owner and project resource types - billing admins,e.t.c.. should not be using/showing this component
  const isOrgOwner = !resourceType

  const cloudApiAccessDescription = (
    <CloudApiAccessDescription
      resourceType={resourceType}
      isApiKey={isApiKey}
      isSpecificResource={!!projectId}
    />
  )

  const cloudEsKibanaApiAccessDescription = (
    <CloudEsKibanaApiAccessDescription
      resourceType={resourceType}
      isApiKey={isApiKey}
      isSpecificResource={!!projectId}
    />
  )

  const apiAccessOptions: Array<EuiSuperSelectOption<string>> = [
    {
      value: ApiAccessOptions.CloudApi,
      inputDisplay: (
        <EuiText size='s'>
          <FormattedMessage
            {...getCloudAPIAccessLabel({ apiAccess: ApiAccessOptions.CloudApi, isApiKey })}
          />
        </EuiText>
      ),
      dropdownDisplay: (
        <Fragment>
          <strong>
            <FormattedMessage
              {...getCloudAPIAccessLabel({ apiAccess: ApiAccessOptions.CloudApi, isApiKey })}
            />
          </strong>
          <EuiText size='s' color='subdued'>
            <p>{cloudApiAccessDescription}</p>
          </EuiText>
        </Fragment>
      ),
    },
    {
      value: ApiAccessOptions.CloudElasticsearchKibanaApi,
      inputDisplay: (
        <EuiText size='s' className='eui-textTruncate'>
          <FormattedMessage
            {...getCloudAPIAccessLabel({
              apiAccess: ApiAccessOptions.CloudElasticsearchKibanaApi,
              isApiKey,
            })}
          />
        </EuiText>
      ),
      dropdownDisplay: (
        <Fragment>
          <EuiFlexGroup
            justifyContent='spaceBetween'
            alignItems='flexStart'
            gutterSize='xs'
            direction='row'
          >
            <strong>
              <FormattedMessage
                {...getCloudAPIAccessLabel({
                  apiAccess: ApiAccessOptions.CloudElasticsearchKibanaApi,
                  isApiKey,
                })}
              />
            </strong>
            {isOrgOwner && isApiKey && (
              <EuiFlexItem>
                <EuiBetaBadge
                  title='Serverless'
                  label={
                    <FormattedMessage
                      id='roles-assignments.api.access.selector.serverless'
                      defaultMessage='Serverless'
                    />
                  }
                  size='s'
                />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>

          <EuiText size='s' color='subdued'>
            <p>{cloudEsKibanaApiAccessDescription}</p>
          </EuiText>
        </Fragment>
      ),
    },
  ]

  const shouldShowTooltip =
    (containsCustomRoles || containsNonCloudSpecificProjectRoles) &&
    currentValue === ApiAccessOptions.CloudApi

  const tooltipContent = shouldShowTooltip ? (
    <LimitedAccessTooltip isApiKey={isApiKey} />
  ) : undefined

  if (isUIAMEnabled && !isGovCloud) {
    return (
      <Fragment>
        <EuiFlexItem css={css(cssStyle)}>
          <EuiToolTip
            position='top'
            content={tooltipContent}
            css={css({ backgroundColor: euiTheme.colors.backgroundBasePlain })}
          >
            <EuiFormRow
              label={
                includeLabel ? (
                  <ApiAccessSelectorTitle isApiKey={isApiKey} isOrgOwner={isOrgOwner} />
                ) : null
              }
              fullWidth={fullWidth}
              css={
                shouldShowTooltip
                  ? css({
                      '.euiFormControlLayoutIcons svg': {
                        color: euiTheme.colors.borderStrongWarning,
                      },
                    })
                  : undefined
              }
            >
              <EuiSuperSelect
                options={apiAccessOptions}
                valueOfSelected={isDisabled ? undefined : currentValue}
                placeholder={
                  <FormattedMessage
                    id='roles-assignments.api.access.selector.placeholder'
                    defaultMessage='No roles assigned'
                  />
                }
                disabled={isDisabled}
                onChange={onChangeValue}
                itemLayoutAlign='top'
                hasDividers={true}
                data-test-id={
                  projectId ? `api-access-selector-${projectId}` : 'api-access-selector'
                }
                isInvalid={!!shouldShowTooltip}
                css={
                  shouldShowTooltip
                    ? css({
                        '--euiFormControlStateColor': euiTheme.colors.borderStrongWarning,
                        '--euiFormControlStateHoverColor': euiTheme.colors.borderStrongWarning,
                      })
                    : undefined
                }
              />
            </EuiFormRow>
          </EuiToolTip>
        </EuiFlexItem>

        {isOrgOwner && !isApiKey && (
          <EuiFlexItem>
            <EuiSpacer size='m' />
            <EuiCallOut
              announceOnMount={true}
              title={
                <FormattedMessage
                  id='roles-assignments.organization-owner-api-access-callout'
                  defaultMessage='Organization owners always have access to the Cloud Console, Elasticsearch, and Kibana for Hosted deployments.'
                />
              }
              color='primary'
              iconType='info'
              size='s'
            />
          </EuiFlexItem>
        )}
      </Fragment>
    )
  }

  return null
}

export default ApiAccessSelector
